import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE } from "../api/config";

const JOBS_API = `${API_BASE}/zuordnen/api/verschieben-jobs`;
const POLL_MS = 1500;

/*
 * useVerschiebenJobs — Hintergrund-Verschiebe-Jobs (Welle 28).
 *
 * const { jobs, startJob, dismissJob } = useVerschiebenJobs({
 *   pageKey: "dokumente" | "bilder" | "fotos",
 *   onItemResult: (item) => {},  // pro Datei, sobald das Ergebnis im Poll auftaucht
 *   onJobDone: (job) => {},      // einmal, wenn ein Job fertig/fehler/abgebrochen ist
 * });
 *
 * startJob(items) schickt den Batch an POST /verschieben-jobs/start und pollt
 * den Status alle 1,5 s. job_ids liegen in localStorage → laufende Jobs werden
 * nach einem Reload weiterverfolgt. Mehrere Jobs parallel sind erlaubt.
 */
export default function useVerschiebenJobs({ pageKey, onItemResult, onJobDone }) {
  const storageKey = `verschieben_jobs_${pageKey}`;
  const [jobs, setJobs] = useState({}); // job_id -> Status-Objekt vom Backend
  const seenCounts = useRef({});        // job_id -> Anzahl bereits gemeldeter Ergebnisse
  const doneNotified = useRef(new Set());
  const cbRef = useRef({ onItemResult, onJobDone });
  cbRef.current = { onItemResult, onJobDone };

  const readStored = useCallback(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || "[]");
    } catch {
      return [];
    }
  }, [storageKey]);

  const writeStored = useCallback((ids) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(ids));
    } catch {}
  }, [storageKey]);

  const startJob = useCallback(async (items) => {
    if (!items || items.length === 0) return null;
    const r = await fetch(`${JOBS_API}/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ typ: pageKey, dateien: items }),
    });
    const d = await r.json();
    if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
    const jobId = d.job_id;
    seenCounts.current[jobId] = 0;
    setJobs((prev) => ({
      ...prev,
      [jobId]: { job_id: jobId, typ: pageKey, status: "running", total: d.total, done: 0, ok_count: 0, fehler_count: 0, ergebnisse: [] },
    }));
    writeStored([...readStored(), jobId]);
    return jobId;
  }, [pageKey, readStored, writeStored]);

  /* Welle 77: extern gestarteten Job (z.B. POST /fotos/analyse-job) zum
     Polling anmelden — gleiche Status-Route, gleiche Resume-Logik. */
  const registerJob = useCallback((jobId, total = 0) => {
    if (!jobId) return;
    seenCounts.current[jobId] = 0;
    setJobs((prev) => ({
      ...prev,
      [jobId]: { job_id: jobId, typ: pageKey, status: "running", total, done: 0, ok_count: 0, fehler_count: 0, ergebnisse: [] },
    }));
    writeStored([...readStored().filter((id) => id !== jobId), jobId]);
  }, [pageKey, readStored, writeStored]);

  const dismissJob = useCallback((jobId) => {
    setJobs((prev) => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    delete seenCounts.current[jobId];
    doneNotified.current.delete(jobId);
    writeStored(readStored().filter((id) => id !== jobId));
  }, [readStored, writeStored]);

  // Reload-Resume: gespeicherte job_ids wieder aufnehmen
  useEffect(() => {
    const ids = readStored();
    if (ids.length === 0) return;
    setJobs((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        if (!next[id]) next[id] = { job_id: id, typ: pageKey, status: "running", total: 0, done: 0, ok_count: 0, fehler_count: 0, ergebnisse: [] };
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Polling für alle aktiven Jobs
  useEffect(() => {
    const activeIds = Object.values(jobs)
      .filter((j) => j.status === "running")
      .map((j) => j.job_id);
    if (activeIds.length === 0) return undefined;

    const poll = async () => {
      for (const id of activeIds) {
        try {
          const r = await fetch(`${JOBS_API}/status?job_id=${encodeURIComponent(id)}`);
          const d = await r.json();
          if (d.status === "unbekannt") {
            // Job existiert nicht (mehr) — still entfernen
            dismissJob(id);
            continue;
          }
          // Neue Datei-Ergebnisse melden (Längen-Diff)
          const seen = seenCounts.current[id] || 0;
          const ergebnisse = d.ergebnisse || [];
          for (let i = seen; i < ergebnisse.length; i++) {
            try { cbRef.current.onItemResult && cbRef.current.onItemResult(ergebnisse[i]); } catch {}
          }
          seenCounts.current[id] = ergebnisse.length;

          setJobs((prev) => (prev[id] ? { ...prev, [id]: d } : prev));

          if (d.status !== "running" && !doneNotified.current.has(id)) {
            doneNotified.current.add(id);
            try { cbRef.current.onJobDone && cbRef.current.onJobDone(d); } catch {}
          }
        } catch {
          // Netzfehler beim Poll: still, nächster Tick versucht es erneut
        }
      }
    };

    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.values(jobs).filter((j) => j.status === "running").map((j) => j.job_id).join(",")]);

  return { jobs, startJob, registerJob, dismissJob };
}
